import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api, qs } from '../lib/api';
import { useApp } from '../lib/app-context';
import { CITIES, today } from '../lib/format';
import { CityTile, ListingCard, Select, Spinner } from '../components/ui';
import {
  BedIcon,
  CashIcon,
  CheckIcon,
  ChevronIcon,
  HouseCheckIcon,
  IdCardIcon,
  MoonIcon,
  PeopleIcon,
} from '../components/icons';
import type { Facets, SearchResponse } from '../lib/types';
import { t } from '../lib/i18n';

type Mode = 'MONTHLY' | 'COLOCATION' | 'NIGHTLY';

/*
 * Short on purpose: three pills must fit a 320px screen without wrapping, in
 * every language.
 */
const MODES: { value: Mode; label: string; icon: ReactNode }[] = [
  { value: 'MONTHLY', label: t('Chambre'), icon: <BedIcon /> },
  { value: 'COLOCATION', label: t('Coloc'), icon: <PeopleIcon /> },
  { value: 'NIGHTLY', label: t('Nuitée'), icon: <MoonIcon /> },
];

const LAST_SEARCH = 'bedly.lastSearch';
const isMode = (value: unknown): value is Mode => value === 'MONTHLY' || value === 'COLOCATION' || value === 'NIGHTLY';

/** The previous visit's mode and city: from the URL first, then this browser. */
function lastSearch(params: URLSearchParams): { mode: Mode; city: string } {
  const fromUrl = params.get('mode');
  if (isMode(fromUrl) || params.get('city')) {
    return { mode: isMode(fromUrl) ? fromUrl : 'MONTHLY', city: params.get('city') ?? '' };
  }
  try {
    const saved = JSON.parse(localStorage.getItem(LAST_SEARCH) ?? 'null') as { mode?: unknown; city?: unknown } | null;
    return {
      mode: isMode(saved?.mode) ? saved.mode : 'MONTHLY',
      city: typeof saved?.city === 'string' ? saved.city : '',
    };
  } catch {
    return { mode: 'MONTHLY', city: '' };
  }
}

/**
 * The front door, built to get a phone user searching in one or two taps:
 * pick a kind of stay, tap a city, done. Everything else is optional and folded
 * away, and the newest listings peek above the tab bar so it is obvious the
 * page goes on.
 */
export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, toast, reportError } = useApp();
  const [params, setParams] = useSearchParams();
  const initial = useRef(lastSearch(params)).current;
  const [facets, setFacets] = useState<Facets | null>(null);
  const [featured, setFeatured] = useState<SearchResponse | null>(null);
  const [mode, setMode] = useState<Mode>(initial.mode);
  const [city, setCity] = useState(initial.city);
  const [when, setWhen] = useState('');
  const [budget, setBudget] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const [alerted, setAlerted] = useState(false);

  /*
   * The choice lives in the URL (so back, reload and a shared link keep it) and
   * in this browser (so opening bedly.ma again lands on the last search).
   */
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set('mode', mode);
    if (city) next.set('city', city);
    else next.delete('city');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    try {
      localStorage.setItem(LAST_SEARCH, JSON.stringify({ mode, city }));
    } catch {
      // A browser without storage still has the URL.
    }
    setAlerted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, city]);

  useEffect(() => {
    void (async () => {
      try {
        const [facetsResponse, featuredResponse] = await Promise.all([
          api.get<Facets>('/search/facets'),
          api.get<SearchResponse>('/search?limit=8&sort=recent'),
        ]);
        setFacets(facetsResponse);
        setFeatured(featuredResponse);
      } catch {
        setFeatured({ items: [], nextCursor: null, hasMore: false });
      }
    })();
  }, []);

  const cityNames = facets?.cities.map((row) => row.city) ?? CITIES;

  const search = (target = { mode, city }) => {
    // Saved here too: a chip tap leaves the page before the effect above runs.
    try {
      localStorage.setItem(LAST_SEARCH, JSON.stringify(target));
    } catch {
      // The URL still carries it.
    }
    navigate(
      `/recherche${qs({
        mode: target.mode,
        city: target.city,
        ...(when ? { startDate: when } : {}),
        ...(budget ? { maxPriceCentimes: Number(budget) * 100 } : {}),
      })}`,
    );
  };

  // What the button will find: listings in this city offering this kind of stay.
  const count = city && facets?.cityModes ? (facets.cityModes[city]?.[mode] ?? 0) : null;
  const nothingYet = count === 0;

  /*
   * Nothing here yet: rather than a dead end, the button offers to watch the
   * search. Someone signed out goes to sign in and comes back to finish it.
   */
  const alertMe = async () => {
    if (!user) {
      navigate('/connexion', { state: { from: `/${qs({ mode, city, alerte: 1 })}` } });
      return;
    }
    setAlerting(true);
    try {
      await api.post('/alerts', { city, mode });
      setAlerted(true);
      toast(t('C’est noté : vous serez alerté dès qu’un logement arrive à {city}.', { city: t(city) }), 'ok');
    } catch (error) {
      reportError(error);
    } finally {
      setAlerting(false);
    }
  };

  // Back from signing in with ?alerte=1: finish what they asked for.
  useEffect(() => {
    if (!user || params.get('alerte') !== '1' || !city) return;
    const next = new URLSearchParams(params);
    next.delete('alerte');
    setParams(next, { replace: true });
    void alertMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.search]);

  const nightly = mode === 'NIGHTLY';

  return (
    <>
      <section className="hero hero--compact">
        <div className="hero__inner">
          <h1 className="hero__title">{t('Ta chambre au Maroc, en 2 minutes')}</h1>
          <p className="hero__lead">{t('Colocations, chambres au mois et nuitées.')}</p>

          <div className="card search-card">
            <div className="mode-pills" role="radiogroup" aria-label={t('Type de séjour')}>
              {MODES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={mode === option.value}
                  className={`mode-pill ${mode === option.value ? 'mode-pill--on' : ''}`}
                  onClick={() => setMode(option.value)}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>

            {/* One tap: a city chip searches straight away with the mode above. */}
            <div className="city-chips" aria-label={t('Villes')}>
              {cityNames.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`city-chip ${city === name ? 'city-chip--on' : ''}`}
                  // Straight to the results. Setting the city here as well would
                  // rewrite this page's URL mid-navigation and cancel it.
                  onClick={() => search({ mode, city: name })}
                >
                  {t(name)}
                </button>
              ))}
            </div>

            <form
              className="search-card__form"
              onSubmit={(event) => {
                event.preventDefault();
                if (nothingYet) void alertMe();
                else search();
              }}
            >
              <label className="field">
                <span className="field__label">{t('Ville')}</span>
                <Select
                  value={city}
                  onChange={setCity}
                  options={[
                    { value: '', label: t('Toutes les villes') },
                    ...cityNames.map((name) => ({ value: name, label: t(name) })),
                  ]}
                />
              </label>

              <button
                type="button"
                className="more-filters"
                aria-expanded={moreOpen}
                aria-controls="more-filters"
                onClick={() => setMoreOpen((open) => !open)}
              >
                <span>
                  {t('Plus de filtres')}
                  {when || budget ? <span className="more-filters__dot" aria-hidden /> : null}
                </span>
                <ChevronIcon className={`more-filters__chevron ${moreOpen ? 'is-open' : ''}`} />
              </button>

              {moreOpen ? (
                <div id="more-filters" className="more-filters__panel">
                  <label className="field">
                    <span className="field__label">{nightly ? t('Arrivée') : t('Emménagement')}</span>
                    {/*
                      A date field has no placeholder of its own, so "Quand ?"
                      is drawn over it while it is empty. It stays a real date
                      field throughout, so one tap opens the phone's picker.
                    */}
                    <span className="date-field">
                      <input
                        className={`input input--date ${when ? '' : 'input--empty'}`}
                        type="date"
                        aria-label={nightly ? t('Arrivée') : t('Emménagement')}
                        min={today()}
                        value={when}
                        onChange={(event) => setWhen(event.target.value)}
                      />
                      {when ? null : (
                        <span className="date-field__placeholder" aria-hidden>
                          {t('Quand ?')}
                        </span>
                      )}
                    </span>
                  </label>
                  <label className="field">
                    <span className="field__label">
                      {nightly ? t('Budget max (DH/nuit)') : t('Budget max (DH/mois)')}
                    </span>
                    <input
                      className="input"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder={nightly ? t('Ex : 400 DH') : t('Ex : 2 500 DH')}
                      value={budget}
                      onChange={(event) => setBudget(event.target.value.replace(/\D/g, ''))}
                    />
                  </label>
                </div>
              ) : null}

              <button
                type="submit"
                className={`btn btn--block search-card__submit ${nothingYet ? 'btn--teal' : ''}`}
                disabled={alerting || (nothingYet && alerted)}
              >
                {nothingYet
                  ? alerted
                    ? t('Alerte activée')
                    : t('Être alerté quand un logement arrive')
                  : count === null
                    ? t('Rechercher')
                    : count === 1
                      ? t('Voir 1 logement')
                      : t('Voir {count} logements', { count })}
              </button>
            </form>
          </div>

          <ul className="trust-chips">
            {[t('Hôtes vérifiés'), t('Logements visités'), t('Loyer payé en main propre')].map((label) => (
              <li key={label} className="trust-chip">
                <CheckIcon />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="page stack stack--loose landing-below">
        {/* First below the hero, so its cards peek above the tab bar. */}
        {featured === null ? (
          <section className="stack stack--tight">
            <h2 className="rail-title">{t('Nouveaux logements')}</h2>
            <Spinner label={t("Chargement des annonces…")} />
          </section>
        ) : featured.items.length > 0 ? (
          <section className="stack stack--tight">
            <div className="row row--between">
              <h2 className="rail-title">{t('Nouveaux logements')}</h2>
              <Link to={`/recherche${qs({ mode })}`} className="rail-more">
                {t("Voir tout")}
              </Link>
            </div>
            <div className="listing-rail">
              {featured.items.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          </section>
        ) : (
          /* Nothing published yet. Rather than an empty grid or an apology, the
             page asks for the thing it is missing. */
          <section
            className="card card--pad stack center"
            style={{ alignItems: 'center', padding: 'clamp(28px, 5vw, 48px)' }}
          >
            <span className="brand__mark" aria-hidden style={{ width: 44, height: 44 }} />
            <h2>{t("Les premières annonces arrivent")}</h2>
            <p className="muted" style={{ maxWidth: 460, margin: 0 }}>
              {t("Bedly ouvre dans")} {facets?.cities.length ?? 0} {t("villes. Aucune chambre n’est encore en ligne — si vous avez une chambre, une colocation ou un logement à louer, vous pouvez être le premier.")}
            </p>
            <div className="row row--wrap" style={{ gap: 10, justifyContent: 'center' }}>
              <Link className="btn" to="/devenir-hote">
                {t("Publier un logement")}
              </Link>
              <Link className="btn btn--ghost" to="/colocations/publier">
                {t("Chercher une colocation")}
              </Link>
            </div>
          </section>
        )}

        <section className="stack">
          <div className="row row--between">
            <h2>{t("Explorer par ville")}</h2>
            {facets?.cities.length ? (
              <span className="small muted">
                {facets.cities.length} {t("villes couvertes")}
              </span>
            ) : null}
          </div>
          <div className="grid grid--tiles">
            {(facets?.cities ?? CITIES.map((name) => ({ city: name, count: 0 }))).map((row) => (
              <CityTile
                key={row.city}
                city={row.city}
                count={row.count}
                onClick={() => navigate(`/recherche${qs({ city: row.city })}`)}
              />
            ))}
          </div>
          {/* The licences of these photos require crediting their authors where
              they appear; this line is that credit, kept out of the footer. */}
          <p className="tiny muted" style={{ margin: 0 }}>
            {t("Photos : Wikimedia Commons, sous licences libres ·")}{' '}
            <Link to="/credits-photos">{t("crédits")}</Link>
          </p>
        </section>

        {facets?.universities.length ? (
          <section className="stack">
            <h2>{t("Près de votre université")}</h2>
            <div className="row row--wrap">
              {facets.universities.map((university) => (
                <button
                  key={university.id}
                  type="button"
                  className="chip"
                  onClick={() =>
                    navigate(
                      `/recherche${qs({
                        universityId: university.id,
                        maxDistanceMetres: 3000,
                        sort: 'distance',
                      })}`,
                    )
                  }
                >
                  {university.shortName ?? university.name}
                  <span className="muted" style={{ fontWeight: 500 }}>
                    {t(university.city)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}


        <section className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          {[
            {
              icon: <IdCardIcon />,
              title: t("CIN vérifiée"),
              body: t("Chaque hôte est identifié par sa carte nationale avant publication."),
            },
            {
              icon: <HouseCheckIcon />,
              title: t("Logements visités"),
              body: t("Notre équipe se déplace sous 48 h et active le badge « Visité »."),
            },
            {
              icon: <CashIcon />,
              title: t("Pas d’avance en ligne"),
              body:
                t("Vous réglez uniquement les frais de service Bedly. Le loyer se paie en espèces à l’hôte, à la remise des clés."),
            },
          ].map((item) => (
            <div key={item.title} className="card card--pad stack stack--tight">
              <div className="row" style={{ gap: 12 }}>
                <span className="trust-badge">{item.icon}</span>
                <h3>{item.title}</h3>
              </div>
              <p className="small muted" style={{ margin: 0 }}>
                {item.body}
              </p>
            </div>
          ))}
        </section>

        <section className="card card--pad row row--between row--wrap" style={{ gap: 16 }}>
          <div className="stack stack--tight">
            <h2>{t("Vous avez une chambre libre ?")}</h2>
            <p className="small muted" style={{ margin: 0 }}>
              {t("Publiez gratuitement. Commission de 8 % du premier mois, uniquement si vous êtes réservé.")}
            </p>
          </div>
          <Link to="/devenir-hote" className="btn">
            {t("Devenir hôte")}
          </Link>
        </section>
      </div>
    </>
  );
}
